/**
 * Full-page site search — full-text across all content types.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getProductImage } from '../utils/imageMapping';
import { getIcon } from '../utils/icons';
import { runSiteSearch, tokenizeQuery } from '../utils/siteSearch';
import { logSearchActivity } from '../utils/userActivity';
import { SITE_SEARCH_STATIC_PAGES } from '../utils/siteSearchStaticPages';
import './AdvancedSearchPage.css';

const PRODUCTS_URL = '/api/products';
const ARTICLES_URL = '/api/content/articles';
const TUNES_URL = '/api/content/tunes?includeIndexHtml=1';
const DEBOUNCE_MS = 320;

const TYPE_ORDER = ['product', 'article', 'tune', 'page'];

const TYPE_LABELS = {
  product: 'Products',
  article: 'Articles',
  tune: 'Tunes & Indexes',
  page: 'Site Pages',
};

const TYPE_LABELS_SHORT = {
  product: 'Products',
  article: 'Articles',
  tune: 'Tunes',
  page: 'Pages',
};

const PAGE_SIZE_OPTIONS = [25, 50, 75];

const TAG_LABELS = {
  product: 'Product',
  article: 'Article',
  page: 'Page',
};

function groupResults(results) {
  const groups = {};
  for (const r of results) {
    if (!groups[r.type]) groups[r.type] = [];
    groups[r.type].push(r);
  }
  return groups;
}

function normTuneType(t) {
  if (!t) return 'Other';
  return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
}

function getTuneSubcats(tuneList) {
  const counts = {};
  for (const t of tuneList) {
    const key = normTuneType(t.tuneType);
    counts[key] = (counts[key] || 0) + 1;
  }
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .map(([label, count]) => ({ label, count }));
}

function TuneSubcatsBar({ tunes, activeTuneType, onSelect }) {
  const subcats = getTuneSubcats(tunes);
  if (!subcats.length) return null;
  return (
    <div className="asp-tune-subcats">
      {subcats.map(({ label, count }) => (
        <button
          key={label}
          className={`asp-tune-subcat${activeTuneType === label ? ' active' : ''}`}
          onClick={() => onSelect(activeTuneType === label ? null : label)}
          aria-pressed={activeTuneType === label}
        >
          <span className="asp-tune-subcat-count">{count}</span> {label}
        </button>
      ))}
    </div>
  );
}

function highlight(text, query) {
  if (!query || !text) return text;
  const tokens = tokenizeQuery(query);
  if (!tokens.length) return text;
  // Build a single regex that alternates all tokens
  const pattern = tokens
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');
  const parts = String(text).split(new RegExp(`(${pattern})`, 'gi'));
  const tokenSet = new Set(tokens.flatMap((t) =>
    t.length > 3 && t.endsWith('s') ? [t, t.slice(0, -1)] : [t]
  ));
  return parts.map((part, i) =>
    tokenSet.has(part.toLowerCase())
      ? <mark key={i} className="asp-highlight">{part}</mark>
      : part
  );
}

function TuneMetaChips({ result }) {
  const chips = [
    result.tuneType      && { label: result.tuneType,        cls: 'asp-chip-tunetype' },
    result.keySignature  && { label: result.keySignature,    cls: 'asp-chip-key' },
    result.collectionBook && { label: result.collectionBook, cls: 'asp-chip-book' },
    result.recordingAlbum && { label: result.recordingAlbum, cls: 'asp-chip-album' },
    result.composer      && { label: result.composer,        cls: 'asp-chip-composer' },
    result.region        && { label: result.region,          cls: 'asp-chip-region' },
  ].filter(Boolean);
  if (!chips.length) return null;
  return (
    <div className="asp-tune-chips">
      {chips.map((c, i) => (
        <span key={i} className={`asp-tune-chip ${c.cls}`}>{c.label}</span>
      ))}
    </div>
  );
}

function ResultCard({ result, searchTerm, onClick }) {
  const title = result.type === 'product' ? result.name : result.title;
  const description = result.description || '';
  const tag = result.type === 'tune'
    ? (result.tuneTag || '[Tune]').replace(/^\[|\]$/g, '')
    : TAG_LABELS[result.type] || result.type;

  return (
    <div
      className="asp-result-card"
      role="listitem"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      }}
    >
      {result.type === 'product' && (
        <div className="asp-result-thumb-wrap">
          {result.image
            ? <img src={getProductImage(result)} alt="" className="asp-result-thumb" />
            : <div className="asp-result-thumb-placeholder">{getIcon('image') || '🎵'}</div>
          }
        </div>
      )}
      <div className="asp-result-body">
        <div className="asp-result-top">
          <span className="asp-result-title">{highlight(title, searchTerm)}</span>
          <span className={`asp-tag asp-tag-${result.type}`}>{tag}</span>
        </div>
        {result.type === 'product' && (
          <div className="asp-product-meta">
            {result.price_cents != null && (
              <span className="asp-product-price">
                ${(result.price_cents / 100).toFixed(2)}
              </span>
            )}
            {result.author && (
              <span className="asp-product-author">{result.author}</span>
            )}
            {result.category && (
              <span className="asp-product-category">{result.category}</span>
            )}
          </div>
        )}
        {description && (
          <p className="asp-result-desc">{highlight(String(description).slice(0, 160), searchTerm)}</p>
        )}
        {result.type === 'tune' && <TuneMetaChips result={result} />}
      </div>
    </div>
  );
}

export default function AdvancedSearchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const inputRef = useRef(null);

  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [tunes, setTunes] = useState([]);
  const [indexLoading, setIndexLoading] = useState(true);

  const [draft, setDraft] = useState(() => searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [activeQuery, setActiveQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [activeTuneType, setActiveTuneType] = useState(null);

  const debounceRef = useRef(null);

  // Restore query from navigation state
  useEffect(() => {
    const stateQ = location.state?.q;
    if (typeof stateQ === 'string' && stateQ.trim()) {
      setDraft(stateQ.trim());
    }
  }, [location.state]);

  // Load products and articles immediately; load tunes with full index HTML in one fetch.
  // Previously two separate tunes fetches ran in parallel and the lightweight one could
  // finish last, overwriting the index_html data — causing book-index full-text to fail.
  useEffect(() => {
    fetch(PRODUCTS_URL).then((r) => r.json()).catch(() => []).then(setProducts);
    fetch(ARTICLES_URL).then((r) => r.json()).catch(() => []).then(setArticles);
    fetch(TUNES_URL)
      .then((r) => r.json())
      .catch(() => [])
      .then((rows) => { setTunes(rows); setIndexLoading(false); });
  }, []);

  const executeSearch = useCallback((q) => {
    const trimmed = q.trim();
    if (!trimmed) { setResults(null); setActiveQuery(''); setActiveFilter('all'); setPage(1); return; }
    setActiveQuery(trimmed);
    setResults(
      runSiteSearch({
        searchText: trimmed,
        scope: 'all',
        products,
        articles,
        tunes,
        staticPages: SITE_SEARCH_STATIC_PAGES,
        tuneTypeFilter: 'all',
      })
    );
    setSearchParams({ q: trimmed }, { replace: true });
    setActiveTuneType(null);
    logSearchActivity(trimmed, `/search?q=${encodeURIComponent(trimmed)}`);
  }, [products, articles, tunes, setSearchParams]);

  // Deep links & browser history: keep the input in sync with ?q= (no overwrite while URL unchanged)
  useEffect(() => {
    const fromUrl = (searchParams.get('q') ?? '').trim();
    if (fromUrl !== (draft || '').trim()) {
      setDraft(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to URL changes, not every keystroke
  }, [searchParams]);

  // Debounced live search (wait for tune index + lists so ?q= hits full corpus)
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!draft.trim()) {
      setResults(null);
      setActiveQuery('');
      setSearchParams((prev) => (prev.get('q') ? new URLSearchParams() : prev), { replace: true });
      return;
    }
    if (indexLoading) {
      return;
    }
    debounceRef.current = setTimeout(() => {
      executeSearch(draft);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [draft, products, articles, tunes, indexLoading, executeSearch, setSearchParams]);

  const handleSubmit = (e) => {
    e.preventDefault();
    clearTimeout(debounceRef.current);
    executeSearch(draft);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    setDraft('');
    setResults(null);
    setActiveQuery('');
    setActiveFilter('all');
    setPage(1);
    setActiveTuneType(null);
    setSearchParams({}, { replace: true });
    inputRef.current?.focus();
  };

  const groups = results ? groupResults(results) : {};
  const totalCount = results ? results.length : 0;

  // Apply tune-type sub-filter if active
  const effectiveGroups = (activeTuneType && groups.tune?.length)
    ? { ...groups, tune: groups.tune.filter((t) => normTuneType(t.tuneType) === activeTuneType) }
    : groups;

  // Flat list for the active filter (used for pagination)
  const activeResults = activeFilter === 'all'
    ? TYPE_ORDER.flatMap((t) => effectiveGroups[t] || [])
    : (effectiveGroups[activeFilter] || []);
  const totalFiltered = activeResults.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedResults = activeResults.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Re-group the paged slice so we can render section headings in "all" mode
  const pagedGroups = groupResults(pagedResults);
  const visibleTypes = activeFilter === 'all'
    ? TYPE_ORDER.filter((t) => pagedGroups[t]?.length > 0)
    : TYPE_ORDER.filter((t) => t === activeFilter && pagedGroups[t]?.length > 0);

  const handleFilterChange = (f) => { setActiveFilter(f); setPage(1); setActiveTuneType(null); };
  const handlePageSizeChange = (n) => { setPageSize(n); setPage(1); };

  return (
    <div className="asp-page">
      <Helmet>
        <title>Search — Cranford Publications</title>
        <meta name="description" content="Full-text search across products, articles, tunes, indexes, and site pages." />
      </Helmet>

      <div className="asp-hero">
        <h1 className="asp-hero-title">Search</h1>

        <form className="asp-search-bar" onSubmit={handleSubmit}>
          <div className="asp-input-wrap">
            <input
              ref={inputRef}
              id="asp-main-input"
              type="search"
              className="asp-input"
              placeholder="Search products, tunes, articles, indexes…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoComplete="off"
              autoFocus
              aria-label="Search"
            />
            {draft && (
              <button
                type="button"
                className="asp-clear-btn"
                onClick={handleClear}
                aria-label="Clear search"
              >×</button>
            )}
          </div>
          <button type="submit" className="asp-submit-btn">Search</button>
        </form>

        {indexLoading && draft.trim() && (
          <p className="asp-index-notice">Loading full index text for richer results…</p>
        )}
      </div>

      <div className="asp-results-area">
        {results === null && !draft.trim() && (
          <div className="asp-empty-state">
            <span className="asp-empty-icon">{getIcon('search')}</span>
            <p>Type keywords to search across products, articles, tunes, indexes, and site pages.</p>
          </div>
        )}

        {results !== null && totalCount === 0 && (
          <div className="asp-no-results">
            <p>No results for <strong>"{activeQuery}"</strong>.</p>
            <p className="asp-no-results-hint">Try different keywords or a shorter phrase.</p>
          </div>
        )}

        {results !== null && totalCount > 0 && (
          <>
            {/* ── Toolbar: filters + summary + page-size ── */}
            <div className="asp-toolbar">
              <div className="asp-filter-bar" role="tablist" aria-label="Filter results by type">
                <button
                  role="tab"
                  aria-selected={activeFilter === 'all'}
                  className={`asp-filter-btn${activeFilter === 'all' ? ' active' : ''}`}
                  onClick={() => handleFilterChange('all')}
                >
                  All <span className="asp-filter-count">{totalCount}</span>
                </button>
                {TYPE_ORDER.filter((t) => groups[t]?.length > 0).map((t) => (
                  <button
                    key={t}
                    role="tab"
                    aria-selected={activeFilter === t}
                    className={`asp-filter-btn asp-filter-btn-${t}${activeFilter === t ? ' active' : ''}`}
                    onClick={() => handleFilterChange(t)}
                  >
                    {TYPE_LABELS_SHORT[t]} <span className="asp-filter-count">{groups[t].length}</span>
                  </button>
                ))}
              </div>

              <div className="asp-toolbar-right">
                <span className="asp-result-summary">
                  {totalFiltered > 0
                    ? `${(safePage - 1) * pageSize + 1}–${Math.min(safePage * pageSize, totalFiltered)} of ${totalFiltered}`
                    : '0'}
                </span>
                <div className="asp-pagesize-group" role="group" aria-label="Results per page">
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <button
                      key={n}
                      className={`asp-pagesize-btn${pageSize === n ? ' active' : ''}`}
                      onClick={() => handlePageSizeChange(n)}
                      aria-pressed={pageSize === n}
                    >{n}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Results ── */}
            {visibleTypes.map((type) => (
              <section key={type} className="asp-group">
                {activeFilter === 'all' ? (
                  <>
                    <h2 className="asp-group-heading">
                      {TYPE_LABELS[type]}
                      <span className="asp-group-count">{groups[type].length}</span>
                    </h2>
                    {type === 'tune' && (
                      <TuneSubcatsBar
                        tunes={groups['tune']}
                        activeTuneType={activeTuneType}
                        onSelect={(t) => { setActiveTuneType(t); setPage(1); }}
                      />
                    )}
                  </>
                ) : type === 'tune' ? (
                  <TuneSubcatsBar
                    tunes={groups['tune']}
                    activeTuneType={activeTuneType}
                    onSelect={(t) => { setActiveTuneType(t); setPage(1); }}
                  />
                ) : null}
                <div className="asp-group-list" role="list">
                  {pagedGroups[type].map((result, idx) => (
                    <ResultCard
                      key={`${result.type}-${result.route}-${result.id ?? idx}`}
                      result={result}
                      searchTerm={activeQuery}
                      onClick={() => navigate(result.route)}
                    />
                  ))}
                </div>
              </section>
            ))}

            {/* ── Pagination ── */}
            {totalPages > 1 && (
              <div className="asp-pagination" role="navigation" aria-label="Search result pages">
                <button
                  className="asp-page-btn"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  aria-label="Previous page"
                >‹</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    className={`asp-page-btn${safePage === p ? ' active' : ''}`}
                    onClick={() => setPage(p)}
                    aria-current={safePage === p ? 'page' : undefined}
                  >{p}</button>
                ))}
                <button
                  className="asp-page-btn"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  aria-label="Next page"
                >›</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


